import { html, svg } from "uhtml";
import { CustomisedWithDialog } from "../custom-element.js";

import { locales } from "../../models/locales.js";
import { locale, tracker } from "../../view-models/app-signals.js";
import { bookInfo } from "../../view-models/bookInfo.js";
import { generateNonce } from "../../models/trackerUtilities.js";

function isDesktop() {
  return !(
    navigator.userAgent.match(/Android/i) ||
    navigator.userAgent.match(/iPhone|iPad|iPod/i)
  );
}

function addParamToUrl(href, prefix, param, sameOrigin) {
  let newHref = new URL(href);

  if (sameOrigin) {
    const isSameOrigin =
      window.location.protocol === newHref.protocol &&
      window.location.port === newHref.port &&
      window.location.hostname === newHref.hostname;
    if (!isSameOrigin) {
      return href;
    }
  }
  if (newHref.search.length > 0) {
    // We need to check that because Edge doesn’t sanitize the search string
    if (newHref.search !== "?") {
      newHref.search += `&${prefix}=${param}`;
    } else {
      newHref.search = `?${prefix}=${param}`;
    }
  } else {
    newHref.search += `?${prefix}=${param}`;
  }

  return newHref.href;
}

// data.url is sharelink
// data.text is bookInfoShareText from locale
// data.title is book title

const icons = {
  share: svg`<svg class="the-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M0 0h24v24H0z" fill="none"></path><path fill="#424242" d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"></path></svg>`,
  copy: svg`<svg class="the-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="#424242" d="M320 448v40c0 13.255-10.745 24-24 24H24c-13.255 0-24-10.745-24-24V120c0-13.255 10.745-24 24-24h72v296c0 30.879 25.121 56 56 56h168zm0-344V0H152c-13.255 0-24 10.745-24 24v368c0 13.255 10.745 24 24 24h272c13.255 0 24-10.745 24-24V128H344c-13.2 0-24-10.8-24-24zm120.971-31.029L375.029 7.029A24 24 0 0 0 358.059 0H352v96h96v-6.059a24 24 0 0 0-7.029-16.97z"></path></svg>`,
  email: svg`<svg class="the-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="none" d="M0 0h24v24H0z"></path><path fill="#424242" d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z"></path></svg>`,
  sms: svg`<svg class="the-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="#424242" d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM9 11H7V9h2v2zm4 0h-2V9h2v2zm4 0h-2V9h2v2z"></path><path d="M0 0h24v24H0z" fill="none"></path></svg>`,
  whatsapp: svg`<svg class="the-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="#075e54" d="M224 122.8c-72.7 0-131.8 59.1-131.9 131.8 0 24.9 7 49.2 20.2 70.1l3.1 5-13.3 48.6 49.9-13.1 4.8 2.9c20.2 12 43.4 18.4 67.1 18.4h.1c72.6 0 133.3-59.1 133.3-131.8 0-35.2-15.2-68.3-40.1-93.2-25-25-58-38.7-93.2-38.7zm77.5 188.4c-3.3 9.3-19.1 17.7-26.7 18.8-12.6 1.9-22.4.9-47.5-9.9-39.7-17.2-65.7-57.2-67.7-59.8-2-2.6-16.2-21.5-16.2-41s10.2-29.1 13.9-33.1c3.6-4 7.9-5 10.6-5 2.6 0 5.3 0 7.6.1 2.4.1 5.7-.9 8.9 6.8 3.3 7.9 11.2 27.4 12.2 29.4s1.7 4.3.3 6.9c-7.6 15.2-15.7 14.6-11.6 21.6 15.3 26.3 30.6 35.4 53.9 47.1 4 2 6.3 1.7 8.6-1 2.3-2.6 9.9-11.6 12.5-15.5 2.6-4 5.3-3.3 8.9-2 3.6 1.3 23.1 10.9 27.1 12.9s6.6 3 7.6 4.6c.9 1.9.9 9.9-2.4 19.1zM400 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48zM223.9 413.2c-26.6 0-52.7-6.7-75.8-19.3L64 416l22.5-82.2c-13.9-24-21.2-51.3-21.2-79.3C65.4 167.1 136.5 96 223.9 96c42.4 0 82.2 16.5 112.2 46.5 29.9 30 47.9 69.8 47.9 112.2 0 87.4-72.7 158.5-160.1 158.5z"></path></svg>`,
  facebook: svg`<svg class="the-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="#3b5998" d="M448 56.7v398.5c0 13.7-11.1 24.7-24.7 24.7H309.1V306.5h58.2l8.7-67.6h-67v-43.2c0-19.6 5.4-32.9 33.5-32.9h35.8v-60.5c-6.2-.8-27.4-2.7-52.2-2.7-51.6 0-87 31.5-87 89.4v49.9h-58.4v67.6h58.4V480H24.7C11.1 480 0 468.9 0 455.3V56.7C0 43.1 11.1 32 24.7 32h398.5c13.7 0 24.8 11.1 24.8 24.7z"></path></svg>`,
  linkedin: svg`<svg class="the-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="#0077b5" d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"></path></svg>`,
  pinterest: svg`<svg class="the-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 496 512"><path fill="#0088cc" d="M248 8C111 8 0 119 0 256s111 248 248 248 248-111 248-248S385 8 248 8zm121.8 169.9l-40.7 191.8c-3 13.6-11.1 16.9-22.4 10.5l-62-45.7-29.9 28.8c-3.3 3.3-6.1 6.1-12.5 6.1l4.4-63.1 114.9-103.8c5-4.4-1.1-6.9-7.7-2.5l-142 89.4-61.2-19.1c-13.3-4.2-13.6-13.3 2.8-19.7l239.1-92.2c11.1-4 20.8 2.7 17.2 19.5z"></path></svg>`,
};

function buttonFactory(services, locale, clickHandler) {
  return services.map((service) => {
    const className = `tool-icon ${service}`;
    return html`<button tabindex="0" class="${className}" data-tool="${service}"
			@click="${clickHandler}">
			${icons[service]}
			<span class="the-icon-title">${locales[service][locale]}</span>
			</button>`;
  });
}

function closeIcon() {
  return svg`<svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" class="icon close" role="img" aria-labelledby="close-icon">
  <use xlink:href="#close-icon"></use>
</svg>`;
}

// Render dialog
// Individual event handler functions for each service
// export dialog id for other opening.
export class CassisSharer extends CustomisedWithDialog(HTMLElement) {
  top = ["copy", "email", "sms"].filter((item) => {
    if ((item === "sms" && !isDesktop()) || item !== "sms") {
      return item;
    }
  });
  services = ["facebook", "whatsapp", "linkedin", "pinterest"];
  nonce = generateNonce();
  static tagName = "cassis-share";

  signals() {
    const info = bookInfo();
    const trackerModel = tracker();
    return {
      locale: locale(),
      title: info.title,
      link: info.shareLink,
      tracker: trackerModel,
    };
  }
  render({ locale, title, link, tracker }) {
    this.title = title;
    this.url = link;
    this.text = locales.bookInfoShareText[locale];
    this.tracker = tracker;
    return html`
			<button id="share-control" aria-hidden="false" data-type="open-dialog">
				<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 19 19" preserveAspectRatio="xMidYMid meet" role="img" class="icon active-icon" aria-labelledby="share-icon">
  <title id="share-icon">${locales.bookInfoShare[locale]}</title>
  <path d="M18.016 1.32v-.127c0-.064-.032-.064-.032-.064s.018 0-.048-.064L17.88 1h.008-.382L1.145 8.669c-.192.064-.256.256-.256.448s.128.319.256.446l4.41 2.046 1.15 6.07c0 .064 0 .064.063.129 0 .062.064.062.064.127.064.001.064.065.129.065h.317s.064 0 .064-.064l.063-.062 3.388-3.652L15.333 16h.19c.063 0 .191-.119.256-.182.127-.064.191-.252.255-.379l2.077-13.898c-.063-.127-.095-.158-.095-.221zM14.5 3.366l-8.691 7.222-3.387-1.599L14.5 3.366zm.32.958-6.198 8.18s0 .064-.064.064l-1.342 3.193-.831-4.408 8.435-7.029zM8.558 15.125l.703-1.727.639.256-1.342 1.471zm6.582-.576-5.368-1.98 7.094-9.458-1.726 11.438z"></path>
</svg>
    <span class="setting-text" id="share-label">${locales.bookInfoShare[locale]}</span>
  </button>

  <dialog closedby="any">

  <button
    autofocus
    @click="${() => this.dialogClose()}"
    aria-label="${locales.closeIcon[locale]}"
    class="modal-view-close"
  >
    ${closeIcon()}
  </button>
    <div class="shareAPIPolyfill-header" @click="${(event) => this.handleShareButtons(event)}">
    <div class="shareAPIPolyfill-header-title">${icons.share} ${
      locales.shareTitle[locale]
    }</div>
    <div class="shareAPIPolyfill-icons-container title">
    ${buttonFactory(this.top, locale, (event) => this.handleShareButtons(event))}
    </div>
    </div>
    <div class="shareAPIPolyfill-body">
    <div class="shareAPIPolyfill-icons-container body">
    ${buttonFactory(this.services, locale, (event) => this.handleShareButtons(event))}
    </div>
    </div>
    </dialog>
    `;
  }
  handleShareButtons(event) {
    const el = event.target;
    if (el.tagName.toLowerCase() === "button") {
      event.preventDefault();
      event.stopPropagation();

      let image = "";
      console.log(this);
      if (this.data?.image) {
        image = encodeURIComponent(this.data.image);
      }

      const tool = el.getAttribute("data-tool");
      if (tool !== null) {
        // We’re first adding the tool as utm-source
        const tmpUrl = addParamToUrl(this.url, "utm_source", tool);
        // And then only the “number once”
        const url = addParamToUrl(tmpUrl, "n", this.nonce);
        const payload = `${this.text}\n\n${url}`;

        if (this.tracker && this.nonce) {
          if (tool === "copy" || tool === "print") {
            this.tracker.handleShare("unknown", this.nonce, tool);
          } else {
            this.tracker.handleShare("successful", this.nonce, tool);
          }
        }

        switch (tool) {
          case "copy": {
            this.handleCopy(payload);
            break;
          }
          case "print": {
            // to ensure it has been closed and the user has a clean view of the page
            queueMicrotask(() => {
              window.print();
            }, 500);
            break;
          }
          case "email": {
            window.open(
              "mailto:" +
                "" +
                "?subject=" +
                encodeURIComponent(this.title) +
                "&body=" +
                encodeURIComponent(payload),
              "_blank",
            );
            break;
          }
          case "sms": {
            window.location.href = `sms:?&body=${encodeURIComponent(payload)}`;
            break;
          }
          case "facebook": {
            window.open(
              "https://www.facebook.com/sharer/sharer.php?" +
                "u=" +
                encodeURIComponent(url) +
                "&quote=" +
                encodeURIComponent(this.text),
              "_blank",
            );
            break;
          }
          case "whatsapp": {
            window.open(
              (isDesktop()
                ? "https://api.whatsapp.com/send?text="
                : "whatsapp://send?text=") + encodeURIComponent(payload),
              "_blank",
            );
            break;
          }
          case "linkedin": {
            window.open(
              `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
                url,
              )}&title=${encodeURIComponent(
                this.title,
              )}&summary=${encodeURIComponent(this.text)}&source=LinkedIn`,
              "_blank",
            );
            break;
          }
          case "pinterest": {
            window.open(
              "https://pinterest.com/pin/create/button/?url=" +
                encodeURIComponent(url) +
                "&description=" +
                encodeURIComponent(this.text) +
                "&media=" +
                image,
              "_blank",
            );
            break;
          }
        }
        this.dialogClose();
      }
    }
  }

  handleCopy(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
    } else {
      // Fallback
      const isIOS = navigator.userAgent.match(/ipad|ipod|iphone/i);

      const el = document.createElement("textarea");
      el.value = text;
      el.setAttribute("readonly", "true");
      el.style.position = "absolute";
      el.style.left = "-9999px";
      document.body.appendChild(el);

      // iOS won’t select programmatically, so we must fake user select
      if (isIOS) {
        el.setSelectionRange(0, el.value.length);
      } else {
        el.select();
      }

      document.execCommand("copy");
      document.body.removeChild(el);
    }
  }
}
CassisSharer.define();
